import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContactCountsResponse, ContactResponse, CreateContactInput, ListContactsQuery, UpdateContactInput } from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

function toQueryString(query: Partial<ListContactsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useContactsQuery(query: Partial<ListContactsQuery>) {
  return useQuery({
    queryKey: ['contacts', query],
    queryFn: () => getPage<ContactResponse[]>(`/contacts${toQueryString(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useContactCountsQuery() {
  return useQuery({
    queryKey: ['contacts', 'counts'],
    queryFn: () => api.get<ContactCountsResponse>('/contacts/counts'),
  });
}

export function useContactQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => api.get<ContactResponse>(`/contacts/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) => api.post<ContactResponse>('/contacts', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

export function useUpdateContact(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContactInput) => api.patch<ContactResponse>(`/contacts/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contacts'] }),
  });
}
